/** Формат рекламы Facebook Instant Games, используемый для независимого lifecycle каждого placement. */
export type FacebookInstantAdFormat = "rewarded" | "interstitial";

/** Дескриптор таймера, возвращаемый внедрённым scheduler. */
export type FacebookInstantAdSlotTimer = ReturnType<typeof globalThis.setTimeout>;

/** Явные зависимости одного lifecycle-слота Facebook Instant Games. */
export interface IFacebookInstantAdSlotDependencies {
	clearTimeout: (timer: FacebookInstantAdSlotTimer) => void;
	createAdInstance: (placementId: string) => Promise<FBInstant.AdInstance>;
	format: FacebookInstantAdFormat;
	isApiAvailable: () => boolean;
	onAvailable: () => void;
	onFailed: () => void;
	onFinished: () => void;
	placementId: string;
	setTimeout: (callback: () => void, delayMs: number) => FacebookInstantAdSlotTimer;
}

type FacebookInstantAdSlotState = "disabled" | "idle" | "loading" | "ready" | "retry_wait" | "showing";

const RETRY_DELAY_MS = {
	default: 30_000,
	frequentLoad: 60_000
} as const;

const PERMANENT_PRELOAD_ERROR_CODES = new Set([
	"CLIENT_UNSUPPORTED",
	"CLIENT_UNSUPPORTED_OPERATION",
	"INVALID_PARAM"
]);
const SLOW_RETRY_PRELOAD_ERROR_CODES = new Set([
	"ADS_FREQUENT_LOAD",
	"ADS_TOO_MANY_INSTANCES",
	"RATE_LIMITED"
]);

/** Извлекает код Facebook-ошибки, не полагаясь на отсутствующие public typings SDK. */
function getFacebookErrorCode(error: unknown): string | undefined {
	if (typeof error === "object" && error != null && "code" in error) {
		const code = (error as { code?: unknown }).code;
		if (typeof code === "string") {
			return code;
		}
	}

	if (typeof error === "string") {
		return error;
	}

	if (error instanceof Error && error.message.length > 0) {
		return error.message;
	}

	return undefined;
}

/** Управляет одноразовым Facebook AdInstance для одного формата и placement. */
export class FacebookInstantAdSlot {
	private readonly _dependencies: IFacebookInstantAdSlotDependencies;
	private _preloadPromise?: Promise<void>;
	private _readyAd?: FBInstant.AdInstance;
	private _retryTimer?: FacebookInstantAdSlotTimer;
	private _state: FacebookInstantAdSlotState = "idle";

	/** Сохраняет явные зависимости slot, чтобы lifecycle не зависел от message channel адаптера. */
	constructor(dependencies: IFacebookInstantAdSlotDependencies) {
		this._dependencies = dependencies;
	}

	/** Запускает первый preload, если slot ещё не disabled и не восстанавливается. */
	public start(): void {
		this.EnsurePreloaded();
	}

	/** Сообщает, существует ли полностью preloaded экземпляр, пригодный для одного показа. */
	public isReady(): boolean {
		return this._state === "ready" && this._readyAd != null;
	}

	/** Показывает atomically consumed экземпляр либо немедленно завершает запрос ошибкой. */
	public async show(): Promise<void> {
		const ad = this._readyAd;
		if (ad == null) {
			log.warn("Facebook Instant %s show failed because no ready ad is available", this._dependencies.format);
			this.InvokeCallback(this._dependencies.onFailed, "show failed callback");
			this.EnsurePreloaded();
			return;
		}

		// Экземпляр Facebook одноразовый, поэтому убираем его до первого await.
		this._readyAd = undefined;
		this._state = "showing";

		let didFinish = false;
		try {
			await ad.showAsync();
			didFinish = true;
		} catch (error) {
			log.error(
				"Facebook Instant %s show failed with code %s: %o",
				this._dependencies.format,
				getFacebookErrorCode(error) ?? "unknown",
				error
			);
		}

		if (didFinish) {
			this.InvokeCallback(this._dependencies.onFinished, "show finished callback");
		} else {
			this.InvokeCallback(this._dependencies.onFailed, "show failed callback");
		}

		// Новый экземпляр готовится в фоне и никогда не задерживает terminal outcome показа.
		this._state = "idle";
		this.EnsurePreloaded();
	}

	/** Запускает не более одного preload или переводит slot в disabled при недоступном API. */
	private EnsurePreloaded(): void {
		if (
			this._state === "disabled" ||
			this._state === "showing" ||
			this._readyAd != null ||
			this._preloadPromise != null ||
			this._retryTimer != null
		) {
			return;
		}

		if (!this.IsApiAvailable()) {
			return;
		}

		this._state = "loading";
		// Promise callback выполняется после присваивания marker, поэтому синхронный throw factory не может его потерять.
		const preloadPromise = Promise.resolve().then(() => this.Preload());
		this._preloadPromise = preloadPromise;
		// Preload обрабатывает все ошибки внутри slot; catch остаётся последней защитой от unhandled rejection.
		void preloadPromise.catch((error: unknown) => {
			log.error("Facebook Instant %s preload lifecycle failed: %o", this._dependencies.format, error);
		});
	}

	/** Создаёт экземпляр и сохраняет его только после успешного loadAsync. */
	private async Preload(): Promise<void> {
		try {
			const ad = await this._dependencies.createAdInstance(this._dependencies.placementId);
			await ad.loadAsync();
			this._readyAd = ad;
			this._state = "ready";
			this.InvokeCallback(this._dependencies.onAvailable, "preload available callback");
		} catch (error) {
			this.HandlePreloadFailure(error);
		} finally {
			this._preloadPromise = undefined;
		}
	}

	/** Обрабатывает preload-ошибку как permanent disable либо как отложенный retry. */
	private HandlePreloadFailure(error: unknown): void {
		this._readyAd = undefined;
		const errorCode = getFacebookErrorCode(error);
		if (errorCode != null && PERMANENT_PRELOAD_ERROR_CODES.has(errorCode)) {
			this.Disable(
				"Facebook Instant %s preload disabled with code %s: %o",
				errorCode,
				error
			);
			return;
		}

		const retryDelayMs =
			errorCode != null && SLOW_RETRY_PRELOAD_ERROR_CODES.has(errorCode)
				? RETRY_DELAY_MS.frequentLoad
				: RETRY_DELAY_MS.default;
		this.ScheduleRetry(error, errorCode, retryDelayMs);
	}

	/** Создаёт единственный delayed retry, не позволяя ошибке preload образовать tight loop. */
	private ScheduleRetry(error: unknown, errorCode: string | undefined, retryDelayMs: number): void {
		if (this._state === "disabled" || this._readyAd != null || this._retryTimer != null) {
			return;
		}

		this._state = "retry_wait";
		log.warn(
			"Facebook Instant %s preload failed with code %s, retrying in %dms: %o",
			this._dependencies.format,
			errorCode ?? "unknown",
			retryDelayMs,
			error
		);

		try {
			this._retryTimer = this._dependencies.setTimeout(() => {
				this._retryTimer = undefined;
				if (this._state === "disabled") {
					return;
				}

				this._state = "idle";
				this.EnsurePreloaded();
			}, retryDelayMs);
		} catch (scheduleError) {
			this.Disable("Facebook Instant %s preload disabled because retry scheduling failed: %o", scheduleError);
		}
	}

	/** Проверяет доступность API и переводит slot в disabled, если Facebook API отсутствует или падает. */
	private IsApiAvailable(): boolean {
		try {
			if (this._dependencies.isApiAvailable()) {
				return true;
			}

			this.Disable("Facebook Instant %s preload disabled because the API is unavailable");
		} catch (error) {
			this.Disable("Facebook Instant %s preload disabled because API availability check failed: %o", error);
		}

		return false;
	}

	/** Отключает slot и очищает возможный retry, чтобы permanent состояние не возобновляло preload. */
	private Disable(message: string, ...argumentsToLog: unknown[]): void {
		this.ClearRetryTimer();
		this._state = "disabled";
		log.error(message, this._dependencies.format, ...argumentsToLog);
	}

	/** Отменяет единственный retry timer при переходе slot в permanent disabled состояние. */
	private ClearRetryTimer(): void {
		if (this._retryTimer == null) {
			return;
		}

		this._dependencies.clearTimeout(this._retryTimer);
		this._retryTimer = undefined;
	}

	/** Изолирует ошибки потребителя callback, чтобы выбранный terminal outcome не мог быть отправлен повторно. */
	private InvokeCallback(callback: () => void, callbackName: string): void {
		try {
			callback();
		} catch (error) {
			log.error("Facebook Instant %s %s threw: %o", this._dependencies.format, callbackName, error);
		}
	}
}
